import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { useState, type ReactNode } from 'react'
import { BottomSheet } from './BottomSheet'
import { OverflowTip } from './Hover/OverflowTip'
import { Pressable } from './Pressable'
import { labelText } from './labelText'
import './sheetSelect.css'

export type SheetSelectOption<T extends string> = {
  value: T
  label: ReactNode
  disabled?: boolean
  className?: string
}

export const SheetSelect = <T extends string>({
  ariaLabel,
  value,
  placeholder = 'Select…',
  options,
  onChange,
  disabled = false,
  triggerClassName,
}: {
  ariaLabel: string
  value: T | null | undefined
  placeholder?: string
  options: Array<SheetSelectOption<T>>
  onChange: (value: T) => void
  disabled?: boolean
  triggerClassName?: string
}) => {
  const [open, setOpen] = useState(false)
  const selected = options.find((option) => option.value === value)
  return (
    <>
      <Pressable
        className={`sheetSelectTrigger ${!selected ? 'isPlaceholder' : ''} ${triggerClassName ?? ''}`}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <OverflowTip label={selected ? labelText(selected.label) : placeholder}>
          <span>{selected?.label ?? placeholder}</span>
        </OverflowTip>
        <ExpandMoreRoundedIcon fontSize="small" aria-hidden />
      </Pressable>
      <BottomSheet isOpen={open} onClose={() => setOpen(false)} title={ariaLabel} className="sheetSelect">
        <div role="listbox" aria-label={ariaLabel} className="sheetSelectListbox">
          {options.map((option) => (
            <Pressable
              key={option.value}
              role="option"
              aria-selected={option.value === value}
              disabled={option.disabled}
              className={`sheetSelectOption ${option.value === value ? 'isSelected' : ''} ${option.className ?? ''}`}
              onClick={() => {
                onChange(option.value)
                setOpen(false)
              }}
            >
              <span>{option.label}</span>
              {option.value === value ? <CheckRoundedIcon fontSize="small" aria-hidden /> : null}
            </Pressable>
          ))}
        </div>
      </BottomSheet>
    </>
  )
}
